import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import api from '../api/client';

// Password policy values mirrored from backend/app/core/password_policy.py.
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 64;
const PASSWORD_BLANK = 'PASSWORD_BLANK';
const PASSWORD_TOO_SHORT = 'PASSWORD_TOO_SHORT';
const PASSWORD_TOO_LONG = 'PASSWORD_TOO_LONG';
const PASSWORD_TOO_COMMON = 'PASSWORD_TOO_COMMON';
const PASSWORD_CONTAINS_PERSONAL_INFO = 'PASSWORD_CONTAINS_PERSONAL_INFO';

// Frontend-only validation code for the confirm password field.
const PASSWORD_MISMATCH = 'PASSWORD_MISMATCH';

// User-facing copy for password validation failures.
const PASSWORD_ERROR_MESSAGES = {
  [PASSWORD_BLANK]: 'Password cannot be blank.',
  [PASSWORD_TOO_SHORT]: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  [PASSWORD_TOO_LONG]: `Password must be no more than ${MAX_PASSWORD_LENGTH} characters.`,
  [PASSWORD_TOO_COMMON]: `Choose a less common password.`,
  [PASSWORD_CONTAINS_PERSONAL_INFO]: `Choose a password that doesn’t include your name, email, or other personal information.`,
  [PASSWORD_MISMATCH]: 'Passwords do not match.',
};

/**
 * Extracts a readable change-password error from FastAPI responses.
 * FastAPI can return detail as a string or as a validation error array.
 */
const getChangePasswordErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === 'string') {
    return detail;
  }

  if (detail.errors.length === 1) {
    return PASSWORD_ERROR_MESSAGES[detail.errors[0]];
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item) => item.msg).filter(Boolean).join(' ') || 'Please check your password and try again.';
  }

  return error.response?.data?.message || 'Unable to change password. Please try again.';
};

/**
 * Validates the new password and confirmation fields before submitting to the backend.
 */
const validatePassword = (newPassword, confirmPassword) => {
  const errors = [];

  // Match the backend blank-password check, which treats whitespace-only values as blank.
  if (!newPassword.trim()) {
    errors.push(PASSWORD_BLANK);
  }

  // Match the backend minimum password length policy.
  if (newPassword.length < MIN_PASSWORD_LENGTH) {
    errors.push(PASSWORD_TOO_SHORT);
  }

  // Match the backend maximum password length policy.
  if (newPassword.length > MAX_PASSWORD_LENGTH) {
    errors.push(PASSWORD_TOO_LONG);
  }

  // Confirm the user typed the same password twice before calling the API.
  if (newPassword !== confirmPassword) {
    errors.push(PASSWORD_MISMATCH);
  }

  return errors;
};

/**
 * ChangePasswordPage Component - A form for logged-in users to change their password.
 */
const ChangePasswordPage = () => {
  // State hooks to manage form inputs, validation, and submission feedback.
  const [currentPassword, setCurrentPassword] = useState(''); // Store current password state
  const [newPassword, setNewPassword] = useState(''); // Store new password state
  const [confirmPassword, setConfirmPassword] = useState(''); // Store confirm password state
  const [error, setError] = useState(''); // Store error messages
  const [message, setMessage] = useState(''); // Store success messages
  const [passwordErrors, setPasswordErrors] = useState([]); // Store password policy validation errors
  const [isSubmitting, setIsSubmitting] = useState(false); // State to track change-password submission

  /**
   * Handles form submission for changing the user's password.
   * Sends the current password and new password to the FastAPI change-password endpoint.
   *
   * @param {React.FormEvent} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Run frontend password checks before calling the backend.
    const validationErrors = validatePassword(newPassword, confirmPassword);
    setPasswordErrors(validationErrors);

    if (validationErrors.length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Send current and new passwords through the refresh-aware authenticated API client.
      const response = await api.post('/api/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      // Show the backend success message and clear sensitive password fields.
      setMessage(response.data?.message || 'Successfully changed password');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors([]);
    } catch (err) {
      // Handle errors if the password change attempt fails.
      setError(getChangePasswordErrorMessage(err));

      // Clear the form inputs after a failed change-password attempt.
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // Main container to center the change password form on the page below the navbar.
    <Container className="d-flex justify-content-center align-items-center change-password-container">
      <Row className="w-100">
        <Col md={6} lg={4} className="mx-auto">
          {/* Card component for displaying the change password form. */}
          <Card className="shadow-sm">
            <Card.Body>
              {/* Logo and heading. */}
              <div className="text-center mb-4">
                <img
                  alt="AGRISMART logo"
                  src="/android-chrome-192x192.png"
                  width="60"
                  height="60"
                  className="text-primary"
                />
                <h2 className="mt-2">Change password</h2>
              </div>

              {/* Form for current password, new password, and password confirmation. */}
              <Form onSubmit={handleSubmit}>
                {/* Current password input field. */}
                <Form.Group controlId="current-password" className="mb-3">
                  <Form.Label>Current password</Form.Label>
                  <Form.Control
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter current password"
                  />
                </Form.Group>

                {/* New password input field. */}
                <Form.Group controlId="new-password" className="mb-3">
                  <Form.Label>New password</Form.Label>
                  <Form.Control
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    maxLength={MAX_PASSWORD_LENGTH}
                    autoComplete="new-password"
                    placeholder="Enter new password"
                  />
                </Form.Group>

                {/* Confirm password input field. */}
                <Form.Group controlId="confirm-password" className="mb-3">
                  <Form.Label>Confirm new password</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    maxLength={MAX_PASSWORD_LENGTH}
                    autoComplete="new-password"
                    placeholder="Confirm new password"
                  />
                </Form.Group>

                {/* Display password policy errors if any. */}
                {passwordErrors.length > 0 && (
                  <Alert variant="danger">
                    {passwordErrors.map((passwordError) => (
                      <div key={passwordError}>{PASSWORD_ERROR_MESSAGES[passwordError]}</div>
                    ))}
                  </Alert>
                )}

                {/* Display change-password endpoint error message if any. */}
                {error && <Alert variant="danger">{error}</Alert>}

                {/* Display success message if the password was changed. */}
                {message && <Alert variant="success">{message}</Alert>}

                {/* Submit button. */}
                <Button variant="primary" type="submit" className="w-100" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Changing password
                    </>
                  ) : (
                    'Change password'
                  )}
                </Button>

                {/* Home navigation. */}
                <div className="text-center mt-3">
                  <Button as={Link} to="/" variant="link" className="p-0">
                    Back to main page
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ChangePasswordPage;
